import { BACKEND_URL } from '@/config/api';

const API = BACKEND_URL;

// FastAPI devuelve `detail` como lista en los 422 (errores de validación)
const errMsg = (d, fallback) => (Array.isArray(d) ? d.map(x => x.msg).join(' · ') : (d || fallback));

class DashboardAPI {
  constructor() {
    this.baseUrl = `${API}/api/dashboard`;
  }

  _headers() {
    const token = sessionStorage.getItem('dashboard_token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  // fetch con mensajes de error comprensibles (en vez de "Failed to fetch")
  async _send(url, opts) {
    try {
      return await fetch(url, opts);
    } catch {
      throw new Error('No hemos podido conectar con el servidor. Revisa tu conexión e inténtalo de nuevo en unos segundos.');
    }
  }

  async _fetch(path, opts = {}) {
    return this._request(`${this.baseUrl}${path}`, opts);
  }

  // Rutas fuera de /api/dashboard (bootcamp, leads) con el mismo token del panel
  async _fetchRoot(path, opts = {}) {
    return this._request(`${API}${path}`, opts);
  }

  async _request(url, opts = {}) {
    const res = await this._send(url, { headers: this._headers(), ...opts });
    if (res.status === 401) {
      sessionStorage.removeItem('dashboard_token');
      sessionStorage.removeItem('dashboard_user');
      // Vuelve a la página en la que estaba después de entrar (y no deja que la pantalla siga con datos vacíos)
      const from = window.location.pathname + window.location.search;
      sessionStorage.setItem('dashboard_return_to', from);
      window.location.href = '/dashboard/login';
      throw new Error('Tu sesión ha caducado. Vuelve a entrar.');
    }
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Error de servidor' }));
      throw new Error(errMsg(err.detail, `Error ${res.status}`));
    }
    return res.json();
  }

  // Auth
  async login(email, password) {
    const res = await this._send(`${this.baseUrl}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      if (res.status === 429) throw new Error('Demasiados intentos. Espera un minuto y vuelve a probar.');
      // Solo un 400/401 del backend es «credenciales incorrectas». Cualquier otra respuesta (sin backend
      // conectado, 404, 405, 5xx) es un problema del servidor y no debe parecer una contraseña mal puesta.
      if (res.status !== 400 && res.status !== 401) {
        throw new Error('No hemos podido conectar con el servidor. Inténtalo de nuevo en unos minutos.');
      }
      const err = await res.json().catch(() => ({}));
      throw new Error(errMsg(err.detail, 'El email o la contraseña no son correctos.'));
    }
    const data = await res.json();
    sessionStorage.setItem('dashboard_token', data.token);
    sessionStorage.setItem('dashboard_user', JSON.stringify(data.user));
    return data;
  }

  // ── Eventos y bootcamp (miembro) ──
  memberEvents() { return this._fetch('/events'); }
  joinEvent(slug, ticketType = 'libre') {
    return this._fetch(`/events/event/${slug}/join`, { method: 'POST', body: JSON.stringify({ ticket_type: ticketType }) });
  }
  reserveBootcamp(editionId, form) {
    return this._fetch(`/events/bootcamp/${editionId}/reserve`, { method: 'POST', body: JSON.stringify(form) });
  }
  // channel: 'particular' | 'empresa' (vía de pago; por defecto, particular)
  checkoutBootcamp(editionId, form, channel) {
    return this._fetch(`/events/bootcamp/${editionId}/checkout`, {
      method: 'POST', body: JSON.stringify({ ...form, ...(channel ? { channel } : {}) }),
    });
  }

  // ── Superadmin ──
  superadminMe() { return this._fetch('/superadmin/me'); }

  // ── CDM (superadmin) ──
  _cdm(path, opts) { return this._fetch(`/superadmin/cdm${path}`, opts); }
  cdmStatus() { return this._cdm('/status'); }
  // anonymize: solo tiene efecto en staging (allí el backend lo aplica siempre). confirm: «REEMPLAZAR» al sustituir el CDM.
  // update: actualizar desde el Sheet conservando lo creado en la plataforma (dryRun = solo simular; confirm «ACTUALIZAR»).
  async cdmImport(file, { anonymize = false, replace = false, update = false, dryRun = false, confirm = '' } = {}) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('anonymize', anonymize ? 'true' : 'false');
    fd.append('replace', replace ? 'true' : 'false');
    fd.append('update', update ? 'true' : 'false');
    fd.append('dry_run', dryRun ? 'true' : 'false');
    fd.append('confirm', confirm || '');
    const token = sessionStorage.getItem('dashboard_token');
    const res = await this._send(`${this.baseUrl}/superadmin/cdm/import`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const d = data.detail;
      const err = new Error(d && typeof d === 'object' && !Array.isArray(d) ? (d.message || 'No se pudo importar el CDM') : errMsg(d, 'No se pudo importar el CDM'));
      if (d && typeof d === 'object' && !Array.isArray(d)) err.detail = d;
      throw err;
    }
    return data;
  }
  cdmClubOperations() { return this._cdm('/club-operations'); }
  cdmCreateClubOperation(data) { return this._cdm('/club-operations', { method: 'POST', body: JSON.stringify(data) }); }
  cdmPatchClubOperation(id, data) { return this._cdm(`/club-operations/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  cdmLiquidateClubOperation(id, data) { return this._cdm(`/club-operations/${encodeURIComponent(id)}/liquidate`, { method: 'POST', body: JSON.stringify(data) }); }
  cdmCreatePosition(data) { return this._cdm('/club-positions', { method: 'POST', body: JSON.stringify(data) }); }
  cdmPatchPosition(id, data) { return this._cdm(`/club-positions/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  cdmWithdrawals() { return this._cdm('/withdrawals'); }
  cdmPatchWithdrawal(id, data) { return this._cdm(`/withdrawals/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  cdmDeletePosition(id) { return this._cdm(`/club-positions/${encodeURIComponent(id)}`, { method: 'DELETE' }); }
  cdmSummary(year, mode, month) { return this._cdm(`/summary?year=${year}&mode=${encodeURIComponent(mode)}&month=${month}`); }
  cdmMonthly(year) { return this._cdm(`/monthly?year=${year}`); }
  cdmMembers() { return this._cdm('/members'); }
  cdmIncludedMember(data) { return this._cdm('/members/included', { method: 'POST', body: JSON.stringify(data) }); }
  cdmPatchMember(id, data) { return this._cdm(`/members/${id}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  cdmBrokers() { return this._cdm('/brokers'); }
  cdmPatchBroker(id, data) { return this._cdm(`/brokers/${id}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  cdmFhct() { return this._cdm('/fhct'); }
  cdmPatchFhct(id, data) { return this._cdm(`/fhct/${id}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  cdmAddBricks(data) { return this._cdm('/bricks/adjustments', { method: 'POST', body: JSON.stringify(data) }); }
  cdmDeleteBricks(id) { return this._cdm(`/bricks/adjustments/${id}`, { method: 'DELETE' }); }
  cdmCommissions() { return this._cdm('/commissions'); }
  cdmMark(data) { return this._cdm('/commissions/mark', { method: 'POST', body: JSON.stringify(data) }); }
  cdmExpenses() { return this._cdm('/expenses'); }
  cdmPatchExpense(id, data) { return this._cdm(`/expenses/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  cdmDeleteExpense(id) { return this._cdm(`/expenses/${encodeURIComponent(id)}`, { method: 'DELETE' }); }
  cdmLinks() { return this._cdm('/links'); }
  cdmLink(cdmId, email) { return this._cdm('/links', { method: 'POST', body: JSON.stringify({ cdm_id: cdmId, email }) }); }
  cdmUnlink(cdmId) { return this._cdm(`/links/${encodeURIComponent(cdmId)}`, { method: 'DELETE' }); }
  cdmOperation(data) { return this._cdm('/operations', { method: 'POST', body: JSON.stringify(data) }); }
  cdmPending() { return this._cdm('/pending'); }
  cdmResolve(data) { return this._cdm('/pending/resolve', { method: 'POST', body: JSON.stringify(data) }); }
  cdmSettings() { return this._cdm('/settings'); }
  cdmPutSettings(data) { return this._cdm('/settings', { method: 'PUT', body: JSON.stringify(data) }); }

  // ── Bootcamp (admin) ──
  bootcampEditions() { return this._fetchRoot('/api/bootcamp/admin/editions'); }
  bootcampCreateEdition(data) { return this._fetchRoot('/api/bootcamp/admin/editions', { method: 'POST', body: JSON.stringify(data) }); }
  bootcampUpdateEdition(id, data) { return this._fetchRoot(`/api/bootcamp/admin/editions/${id}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  bootcampUpdateRegistration(id, data) { return this._fetchRoot(`/api/bootcamp/admin/registrations/${id}`, { method: 'PATCH', body: JSON.stringify(data) }); }
  async bootcampUploadFlyer(id, file) {
    const fd = new FormData();
    fd.append('file', file);
    const token = sessionStorage.getItem('dashboard_token');
    const res = await this._send(`${API}/api/bootcamp/admin/editions/${id}/flyer`, { method: 'PUT', headers: { Authorization: `Bearer ${token}` }, body: fd });
    if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(errMsg(e.detail, 'No se pudo subir el flyer')); }
    return res.json();
  }
  async bootcampReceiptUrl(regId) {
    const token = sessionStorage.getItem('dashboard_token');
    const res = await this._send(`${API}/api/bootcamp/admin/registrations/${regId}/receipt`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) throw new Error('No se pudo abrir el justificante');
    return URL.createObjectURL(await res.blob());
  }

  // ── Formularios antiguos de la web (colección leads): estado y notas desde la ficha de la persona ──
  updateLead(id, data) { return this._fetchRoot(`/api/leads/${id}`, { method: 'PATCH', body: JSON.stringify(data) }); }

  // Recuperación de contraseña (sin sesión)
  async _public(path, body) {
    const res = await this._send(`${this.baseUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.status === 429) throw new Error('Demasiados intentos. Espera un minuto y vuelve a probar.');
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(errMsg(data.detail, 'Ha ocurrido un error. Inténtalo de nuevo.'));
    return data;
  }
  forgotPassword(email) { return this._public('/password/forgot', { email }); }
  resetPassword(token, password) { return this._public('/password/reset', { token, password }); }

  // Endpoints
  me() { return this._fetch('/me'); }
  profile() { return this._fetch('/profile'); }
  updateContactPrivacy(hide) { return this._fetch('/profile/privacy', { method: 'PATCH', body: JSON.stringify({ hide_contact_from_network: hide }) }); }
  metrics() { return this._fetch('/metrics'); }
  sales(status) { return this._fetch(`/sales${status ? `?status=${status}` : ''}`); }
  commissions(status) { return this._fetch(`/commissions${status ? `?status=${status}` : ''}`); }
  courses() { return this._fetch('/courses'); }
  successKit() { return this._fetch('/success-kit'); }
  marketing() { return this._fetch('/marketing'); }
  affiliates() { return this._fetch('/affiliates'); }
  myContacts() { return this._fetch('/my-contacts'); }
  myOperations() { return this._fetch('/my-operations'); }
  requestWithdrawal(data) { return this._fetch('/my-operations/withdrawals', { method: 'POST', body: JSON.stringify(data) }); }
  myNetworkTree() { return this._fetch('/my-network-tree'); }
  changePassword(currentPassword, newPassword) {
    return this._fetch('/password/change', { method: 'POST', body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }) });
  }
  chatMode() { return this._fetchRoot('/api/chat/private/mode'); }

  // Admin: Resumen y Personas (cuenta + tests + inscripciones unidos por email; routes/admin_people.py)
  adminSummary() { return this._fetch('/admin/summary'); }
  _qs(params = {}) {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== null && v !== undefined && v !== '')).toString();
    return qs ? `?${qs}` : '';
  }
  // params: { kind: all|users|tests|registrations, q, tier, test, temp, stage, referrer, event, paid, page, page_size }
  adminPeople(params = {}) { return this._fetch(`/admin/people${this._qs(params)}`); }
  // La misma lista en CSV (todas las páginas): se descarga con el token y se guarda con el nombre que da el servidor
  async adminPeopleExport(params = {}) {
    const res = await this._send(`${this.baseUrl}/admin/people/export${this._qs(params)}`, { headers: this._headers() });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(errMsg(err.detail, 'No se ha podido descargar el CSV'));
    }
    const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') || '')?.[1] || 'personas.csv';
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    return name;
  }
  // Red completa: plataforma y CDM unidos por email, todos los niveles (comisiones solo para el superadmin)
  adminNetwork() { return this._fetch('/admin/network'); }
  adminPerson(key) { return this._fetch(`/admin/people/${encodeURIComponent(key)}`); }
  // Cuentas creadas por bots (routes/admin_bots.py)
  adminBotSuspects() { return this._fetch('/admin/bot-suspects'); }
  adminDeleteBotSuspects(ids) {
    return this._fetch('/admin/bot-suspects/delete', { method: 'POST', body: JSON.stringify({ ids, confirm: 'ELIMINAR' }) });
  }
  adminNotBot(userId) { return this._fetch(`/admin/bot-suspects/${encodeURIComponent(userId)}/not-bot`, { method: 'POST' }); }
  adminDeleteUser(userId) { return this._fetch(`/admin/users/${userId}`, { method: 'DELETE' }); }
  // Qué pasaría al borrar (referidos, leads, CDM…) sin borrar nada
  adminDeleteUserPreview(userId) { return this._fetch(`/admin/users/${userId}?dry_run=true`, { method: 'DELETE' }); }
  adminChangeRole(userId, role) {
    return this._fetch(`/admin/users/${userId}/role`, {
      method: 'PATCH',
      body: JSON.stringify({ role }),
    });
  }
  adminChangeMembership(userId, membership) {
    // membership: null (Free) | 'starter' (Access) | 'basica' (Pro) | 'premium' (Premium)
    return this._fetch(`/admin/users/${userId}/membership`, {
      method: 'PATCH',
      body: JSON.stringify({ membership }),
    });
  }
  adminAssignSponsor(userId, sponsorId) {
    return this._fetch(`/admin/users/${userId}/sponsor`, {
      method: 'PATCH',
      body: JSON.stringify({ sponsor_id: sponsorId || null }),
    });
  }

  // Memberships (Stripe)
  // Precios del panel: solo con sesión (también usuarios sin membresía)
  membershipPlans() { return this._fetch('/memberships/plans'); }
  myMembership() { return this._fetch('/memberships/me'); }
  // channel: 'particular' (Freedom Life Club LLC) | 'empresa' (Inbrindor Tech, S.L., con IVA y factura)
  createCheckout(plan, channel) {
    return this._fetch('/memberships/checkout', {
      method: 'POST',
      body: JSON.stringify({ plan, ...(channel ? { channel } : {}) }),
    });
  }
  getCheckoutSession(sessionId) {
    return this._fetch(`/memberships/session/${sessionId}`);
  }
  cancelSubscription() {
    return this._fetch('/memberships/cancel', { method: 'POST' });
  }
  resumeSubscription() {
    return this._fetch('/memberships/resume', { method: 'POST' });
  }
  // Verificación del email del registro
  sendEmailCode() { return this._fetch('/email/send-code', { method: 'POST' }); }
  verifyEmailCode(code) { return this._fetch('/email/verify', { method: 'POST', body: JSON.stringify({ code }) }); }

  // Documentos legales: KYC, aceptación de T&C y Mis documentos
  legalStatus() { return this._fetch('/legal/status'); }
  legalCurrentDocument(type) { return this._fetch(`/legal/documents/${type}/current`); }
  saveKyc(data) { return this._fetch('/legal/kyc', { method: 'POST', body: JSON.stringify(data) }); }
  acceptLegal(type, version) { return this._fetch('/legal/accept', { method: 'POST', body: JSON.stringify({ type, version }) }); }
  myDocuments() { return this._fetch('/documents'); }
  getDocument(id) { return this._fetch(`/documents/${id}`); }

  // Upgrade a plaza vitalicia (pop-up 500 € / después 1.000 € durante 30 días)
  declineLifetimeOffer() {
    return this._fetch('/memberships/lifetime/decline', { method: 'POST' });
  }
  createLifetimeCheckout() {
    return this._fetch('/memberships/lifetime/checkout', { method: 'POST' });
  }

  // ── Events (admin CRUD + registrations) ──
  adminListEvents() {
    return this._fetch('/admin/events');
  }
  adminCreateEvent(payload) {
    return this._fetch('/admin/events', { method: 'POST', body: JSON.stringify(payload) });
  }
  adminGetEvent(eventId) {
    return this._fetch(`/admin/events/${eventId}`);
  }
  adminUpdateEvent(eventId, patch) {
    return this._fetch(`/admin/events/${eventId}`, { method: 'PATCH', body: JSON.stringify(patch) });
  }
  adminDeleteEvent(eventId) {
    return this._fetch(`/admin/events/${eventId}`, { method: 'DELETE' });
  }
  // Multipart upload — bypass _fetch's JSON content type
  async adminUploadEventFlyer(eventId, file) {
    const token = sessionStorage.getItem('dashboard_token');
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${this.baseUrl}/admin/events/${eventId}/flyer`, {
      method: 'PUT',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: 'Error subiendo el flyer' }));
      throw new Error(errMsg(err.detail, `Error ${res.status}`));
    }
    return res.json();
  }
  // Entrada con cóctel: pagada o pendiente (se cobra fuera de la plataforma)
  adminSetEventRegistrationPayment(regId, paymentStatus) {
    return this._fetch(`/admin/events/registrations/${regId}`, { method: 'PATCH', body: JSON.stringify({ payment_status: paymentStatus }) });
  }

  // ── Test de idoneidad (etapa y notas de cada test; ajustes de la agenda) ──
  adminUpdateFunnelLead(leadId, data) {
    return this._fetch(`/admin/funnel/leads/${leadId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }
  adminFunnelConfig() {
    return this._fetch('/admin/funnel/config');
  }
  adminUpdateFunnelConfig(payload) {
    return this._fetch('/admin/funnel/config', { method: 'PUT', body: JSON.stringify(payload) });
  }

  // Editable copy (admin-managed overrides)
  listCopy() {
    return this._fetch('/copy');
  }
  upsertCopy(key, valueEs, valueEn) {
    return this._fetch(`/copy/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify({ value_es: valueEs, value_en: valueEn || null }),
    });
  }
  deleteCopy(key) {
    return this._fetch(`/copy/${encodeURIComponent(key)}`, { method: 'DELETE' });
  }
}

export const dashboardApi = new DashboardAPI();
